/**
 * PSD viewer — UI layer.
 *
 * The engine returns either RGBA pixels (the composite) or a ready JPEG (the
 * embedded thumbnail). Both become a canvas here, because that is the only
 * place a browser turns pixels back into a file.
 */

import { readPsd, PsdError, errorFamily } from './psd-read.js';

const T = (k, p) => {
  let s = (window.__PO_I18N && window.__PO_I18N[k]) || k;
  if (p) for (const a of Object.keys(p).sort((x, y) => y.length - x.length)) {
    s = s.split(':' + a).join(p[a]);
  }
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

const state = { file: null, result: null, canvas: null, urls: [] };

function show(html) { const o = el('po-out'); o.innerHTML = html; o.hidden = false; }
const card = (tone, title, body, extra = '') =>
  `<div class="po-card po-tone-${tone}"><p class="po-title">${title}</p>
     <p class="po-body">${body}</p>${extra}</div>`;

/** The picture as a canvas, whichever form the engine handed over. */
async function toCanvas(image) {
  const canvas = document.createElement('canvas');
  canvas.width = image.w; canvas.height = image.h;
  const ctx = canvas.getContext('2d');

  if (image.rgba) {
    const img = new ImageData(image.rgba, image.w, image.h);
    ctx.putImageData(img, 0, 0);
    return canvas;
  }
  // A thumbnail arrives as a JPEG; decoding it is the browser's job.
  const blob = new Blob([image.jpeg], { type: 'image/jpeg' });
  const bmp = await createImageBitmap(blob);
  canvas.width = bmp.width; canvas.height = bmp.height;
  ctx.drawImage(bmp, 0, 0);
  bmp.close && bmp.close();
  return canvas;
}

const baseName = (file) => String(file.name || 'document').replace(/\.[^.]+$/, '') || 'document';

async function onFile(file) {
  state.file = file;
  for (const u of state.urls) URL.revokeObjectURL(u);
  state.urls = [];
  show(`<div class="po-working">${T('reading')}</div>`);
  window.__stage && window.__stage('reading');

  let res;
  try {
    res = readPsd(new Uint8Array(await file.arrayBuffer()));
  } catch (err) {
    /* The precise code goes to telemetry, the family to the person: those are
     * two different audiences and the same string cannot serve both. */
    const code = err instanceof PsdError ? err.code : 'error';
    const key = `body_${errorFamily(code)}`;
    show(card('bad', T('verdict_none'), T(key) === key ? T('body_none') : T(key)));
    window.__aeDiagnostic && window.__aeDiagnostic({
      issue: code, ext: (file.name || '').split('.').pop().toLowerCase(), size: file.size });
    return;
  }
  state.result = res;
  await render(res, file);
}

async function render(res, file) {
  const canvas = await toCanvas(res.image);
  state.canvas = canvas;
  const blob = await new Promise((r) => canvas.toBlob(r, 'image/png'));
  const url = URL.createObjectURL(blob);
  state.urls.push(url);

  const h = res.header;
  const profile = res.profile && res.profile.name;
  const facts = [
    [T('fact_size_px'), `${h.width} × ${h.height}`],
    [T('fact_mode'), `${T(`mode_${h.colorMode}`) || h.colorMode}, ${h.depth} ${T('bit')}`],
    profile ? [T('fact_profile'), profile] : null,
    res.layerNames.length ? [T('fact_layers'), String(res.layerNames.length)] : null,
    [T('fact_shown'), T(`shown_${res.image.kind}`)],
    [T('fact_file'), human(file.size)],
  ].filter(Boolean).map(([k, v]) => `<tr><th scope="row">${k}</th><td>${esc(v)}</td></tr>`).join('');

  /* Said only when true. The 32-bit note in particular is the honest one: the
   * file opens and the colours will be off, and pretending otherwise would send
   * someone away thinking their document is ruined. */
  const notes = [];
  if (res.foundByScan) notes.push(T('note_found_by_scan'));
  if (res.image.kind === 'thumbnail') notes.push(T('note_thumbnail_only'));
  if (res.image.complete === false) notes.push(T('note_partial'));
  if (h.depth === 32) notes.push(T('note_32bit'));
  if (h.colorMode === 'cmyk') notes.push(T('note_cmyk'));
  /* Only when the profile is NOT plain sRGB — measured across 273 real files,
   * 131 of the 182 profiles are exactly that, and saying "this has a profile"
   * about sRGB would be noise on half the documents that ever arrive. The
   * linear variant belongs to 32-bit documents, which already have their note. */
  if (profile && !/^sRGB\b/i.test(profile) && h.depth !== 32 && h.colorMode !== 'cmyk') {
    notes.push(T('note_profile', { profile }));
  }
  const notesHtml = notes.length
    ? `<section class="po-block"><h3>${T('notes_title')}</h3>
         <ul class="po-notes">${notes.map((n) => `<li>${n}</li>`).join('')}</ul></section>` : '';

  const layers = res.layerNames.length
    ? `<section class="po-block"><h3>${T('layers_title', { n: String(res.layerNames.length) })}</h3>
         <p class="po-body">${T('layers_desc')}</p>
         <ul class="po-list">${res.layerNames.slice(0, 200).map((n) =>
           `<li>${esc(n || T('layer_unnamed'))}</li>`).join('')}</ul></section>` : '';

  /* A 16×16 icon shown at 16 CSS pixels is not shown at all, and a 101×55 logo
   * stretched to 900 is shown wrong. So: never smooth-upscale, but do enlarge a
   * tiny document by a WHOLE number with `pixelated` — every screen pixel is
   * then a real document pixel, repeated, and nothing is invented. */
  const long = Math.max(canvas.width, canvas.height);
  const zoom = long < 320 ? Math.max(1, Math.floor(320 / long)) : 1;
  /* Width only — height stays `auto` so a narrow phone can shrink it back
   * without squashing the aspect ratio. */
  const sizing = zoom > 1 ? ` class="po-zoom" style="width:${canvas.width * zoom}px"` : '';

  show(card('ok', T('verdict_open', { w: String(res.image.w), h: String(res.image.h) }),
      T('body_open'),
      `<div class="po-shot"><img src="${url}"${sizing} alt="${T('alt_preview')}" decoding="async"></div>
       <div class="po-actions">
         <button type="button" class="po-btn" id="po-png">${T('save_png')}</button>
         <button type="button" class="po-btn po-btn-ghost" id="po-jpg">${T('save_jpg')}</button>
       </div>`)
    + `<section class="po-block"><h3>${T('facts_title')}</h3><table class="po-facts">${facts}</table></section>`
    + notesHtml + layers);

  wire(file);

  window.__stage && window.__stage('analyzed');
  window.__aeToolComplete && window.__aeToolComplete({
    mode: h.colorMode, depth: h.depth, layers: res.layerNames.length,
    w: res.image.w, h: res.image.h, shown: res.image.kind,
    foundByScan: res.foundByScan ? 1 : 0, sizeMB: Math.round(file.size / 1048576),
  });
}

function wire(file) {
  /* The unit is the FILE: saving a PNG and then a JPEG of the same document is
   * one job. */
  let charged = false;
  const bill = () => {
    if (charged || !window.__toolLimit) { charged = true; return { commit() {}, abort() {} }; }
    const key = window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null;
    return window.__toolLimit.begin('psd-open', key);
  };

  const save = (type, ext, quality) => async () => {
    const run = bill();
    if (!run) return;
    try {
      const blob = await new Promise((r) => state.canvas.toBlob(r, type, quality));
      if (!blob) throw new Error('encode_returned_null');
      window.__downloadFile(blob, `${baseName(file)}.${ext}`);
      run.commit();
      window.__aeToolComplete && window.__aeToolComplete({ export: ext });
    } catch (err) {
      run.abort();
      window.__aeToolError && window.__aeToolError({ errorType: 'encode_failed', message: String(err).slice(0, 120) });
      window.__handleError && window.__handleError(err, 'psd-open/save');
    }
  };

  const png = el('po-png');
  if (png) png.addEventListener('click', save('image/png', 'png'));
  const jpg = el('po-jpg');
  if (jpg) jpg.addEventListener('click', save('image/jpeg', 'jpg', 0.92));
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('poDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (ev) => {
    const f = ev.detail && (ev.detail.file || (ev.detail.files && ev.detail.files[0]));
    if (f) onFile(f);
  });
  // The uploader's × removed the file; the preview below describes it, so it goes too.
  zone.addEventListener('tool-uploader:clear', () => {
    const out = el('po-out');
    if (out) { out.innerHTML = ''; out.hidden = true; }
  });
});

export { onFile };
